import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { Check, X, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Header from '../components/blog/Header';
import Footer from '../components/blog/Footer';
import { Container } from '../components/ui/Container';
import { Button } from '../components/ui/Button';
import SEO from '../components/seo/SEO';
import Spinner from '../components/ui/Spinner';
import { unsubscribeFromNewsletter } from '../services/newsletterService';

const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: ${(p) => p.theme.colors.background};
  display: flex;
  flex-direction: column;
`;

const Main = styled.main`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 0;

  ${Container} {
    max-width: 32rem;
  }
`;

const Content = styled.div`
  text-align: center;
  background-color: ${(p) => p.theme.colors.card};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius['2xl']};
  padding: 3rem 2rem;
`;

const IconContainer = styled.div`
  width: 4rem;
  height: 4rem;
  margin: 0 auto 1.5rem;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(p) =>
    p.$type === 'success' ? `${p.theme.colors.success}22` : `${p.theme.colors.error}22`};
  color: ${(p) => (p.$type === 'success' ? p.theme.colors.success : p.theme.colors.error)};

  svg {
    width: 2rem;
    height: 2rem;
  }
`;

const Title = styled.h1`
  font-family: ${(p) => p.theme.typography.fonts.display};
  font-size: 1.75rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const Message = styled.p`
  font-size: 1rem;
  color: ${(p) => p.theme.colors.mutedForeground};
  line-height: 1.7;
  margin-bottom: 2rem;
`;

export default function Unsubscribe() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const run = async () => {
      if (!token) {
        setStatus('error');
        setMessage(t('newsletter.unsubscribeNoToken'));
        return;
      }

      const result = await unsubscribeFromNewsletter(token);
      if (result.success) {
        setStatus('success');
        setMessage(result.message);
      } else {
        setStatus('error');
        setMessage(result.message);
      }
    };

    run();
  }, [token, t]);

  return (
    <PageWrapper>
      <SEO title={t('newsletter.unsubscribeTitle')} description={t('newsletter.unsubscribeTitle')} noindex />
      <Header />
      <Main>
        <Container>
          <Content>
            {status === 'loading' && (
              <>
                <IconContainer>
                  <Spinner size="lg" />
                </IconContainer>
                <Title>{t('newsletter.unsubscribeLoading')}</Title>
                <Message>{t('newsletter.unsubscribeLoadingMessage')}</Message>
              </>
            )}

            {status === 'success' && (
              <>
                <IconContainer $type="success">
                  <Check />
                </IconContainer>
                <Title>{t('newsletter.unsubscribeSuccessTitle')}</Title>
                <Message>{message || t('newsletter.unsubscribeSuccessMessage')}</Message>
                <Button as={Link} to="/" variant="primary">
                  <ArrowLeft />
                  {t('newsletter.backHome')}
                </Button>
              </>
            )}

            {status === 'error' && (
              <>
                <IconContainer $type="error">
                  <X />
                </IconContainer>
                <Title>{t('newsletter.unsubscribeErrorTitle')}</Title>
                <Message>{message || t('newsletter.unsubscribeErrorMessage')}</Message>
                <Button as={Link} to="/" variant="primary">
                  <ArrowLeft />
                  {t('newsletter.backHome')}
                </Button>
              </>
            )}
          </Content>
        </Container>
      </Main>
      <Footer />
    </PageWrapper>
  );
}
